import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Zoom, useMediaQuery, IconButton, AppBar, Toolbar,
  Container, CssBaseline, useScrollTrigger, Box, Fab,
} from "@mui/material";
import { useTheme, alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { ClickAwayListener } from "@mui/base";

import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';

import UserMenu from "./_common/UserMenu";
import Logo from "./_common/Logo";
import MainMenu from "./_common/MainMenu";
import SearchFund from "./_common/SearchFund";
import SwipeableMobileDrawer from "./_common/SwipeableMobileDrawer";
import StepBanner from "./_common/StepBanner";

import getPricePlans from "../../lib/pricing/_getPricePlans";

const SearchTicker = dynamic(() => import('./_common/SearchTicker'));

const groupAdsByPlan = (ads, plan) => {
  if (!Array.isArray(ads)) return [];
  try {
    return ads.filter(ad => ad.active && ad.targetAudience.includes(plan));
  } catch (error) {
    console.error("Error grouping ads:", error.message);
    return [];
  }
};

const INITIAL_ADS_STATE = { top: [], bottom: [], autoplayProps: [] };

function ScrollTop({ children }) {
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 100 });

  const handleClick = useCallback((event) => {
    const anchor = (event.target.ownerDocument || document)
      .querySelector('#back-to-top-anchor');
    if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return (
    <Zoom in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 10 }}
      >
        {children}
      </Box>
    </Zoom>
  );
}

ScrollTop.propTypes = { children: PropTypes.element.isRequired };

const ResponsiveAppBar = ({ pageLoading, plan }) => {
  const theme = useTheme();
  const router = useRouter();

  const isFund = router?.asPath?.includes("/funds/");
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [showSearch, setShowSearch] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [adsData, setAdsData] = useState(INITIAL_ADS_STATE);

  useEffect(() => {
    setHydrated(true);
  }, []);

  useEffect(() => {
    let cancelled = false;

    getPricePlans()
      .then(({ plans } = {}) => {
        if (cancelled) return;
        const premiumPlan = plans?.find(d => d.type === "PREMIUM");
        const features = premiumPlan?.features
          ? JSON.parse(premiumPlan.features)?.banner
          : null;
        if (features?.length > 0) {
          setAdsData({
            top: features[0]?.top_ads ?? [],
            bottom: features[0]?.bottom_ads ?? [],
            autoplayProps: features[0]?.autoplay_props ?? [],
          });
        }
      })
      .catch((error) => console.error("Failed to load pricing banners", error));

    return () => { cancelled = true; };
  }, []);

  const groupedTopAds = useMemo(
    () => groupAdsByPlan(adsData.top, plan),
    [adsData.top, plan]
  );
  const groupedBottomAds = useMemo(
    () => groupAdsByPlan(adsData.bottom, plan),
    [adsData.bottom, plan]
  );
  const stepper = useMemo(
    () => adsData.autoplayProps[0] ?? {},
    [adsData.autoplayProps]
  );

  const handleHideSearch = useCallback(() => setShowSearch(false), []);
  const handleShowSearch = useCallback(() => setShowSearch(true), []);

  const SearchComponent = isFund ? SearchFund : SearchTicker;

  const showMobileSearchIcon = hydrated && isMobile && !showSearch;
  const showInlineSearch = showSearch || !showMobileSearchIcon;

  return (
    <>
      <CssBaseline />
      <Box id="back-to-top-anchor" />
      <StepBanner
        ads={groupedTopAds}
        stepper={stepper}
        pageLoading={pageLoading}
        plan={plan}
      />

      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: alpha('#001742', 0.9),
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          zIndex: (theme) => theme.zIndex.drawer,
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 0.5, md: 3 } }}>
          <Toolbar
            disableGutters
            variant="dense"
            sx={{ minHeight: { xs: 52, md: 56 } }}
          >
            <SwipeableMobileDrawer />

            {!showSearch && (
              <Box sx={{ mr: 'auto', ml: { xs: 0.5, md: 1 } }}>
                <Logo />
              </Box>
            )}

            <ClickAwayListener onClickAway={handleHideSearch}>
              <Box
                sx={{
                  py: showSearch ? 1 : 0,
                  display: 'flex',
                  alignItems: 'center',
                  flex: showSearch ? 1 : 'none',
                }}
              >
                {showInlineSearch ? (
                  <SearchComponent setShowSearch={setShowSearch} />
                ) : showMobileSearchIcon ? (
                  <IconButton
                    aria-label="Open search"
                    color="inherit"
                    onClick={handleShowSearch}
                    sx={{
                      ml: 'auto',
                      color: alpha('#fff', 0.72),
                      transition: 'color 0.15s ease, background-color 0.15s ease',
                      '&:hover': {
                        color: '#fff',
                        bgcolor: 'rgba(255,255,255,0.08)',
                      },
                      '&:focus-visible': {
                        outline: '2px solid rgba(255,255,255,0.55)',
                        outlineOffset: '2px',
                      },
                    }}
                  >
                    <SearchIcon />
                  </IconButton>
                ) : null}
              </Box>
            </ClickAwayListener>

            <MainMenu pageLoading={pageLoading} />
            {!showSearch && <UserMenu pageLoading={pageLoading} />}
          </Toolbar>
        </Container>
      </AppBar>

      <StepBanner
        ads={groupedBottomAds}
        stepper={stepper}
        pageLoading={pageLoading}
        plan={plan}
      />

      <ScrollTop>
        <Fab
          size="small"
          aria-label="Scroll back to top"
          sx={{
            bgcolor: '#e2e8f0',
            color: '#001742',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            '&:hover': { bgcolor: '#cbd5e1' },
          }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: '1.2rem' }} />
        </Fab>
      </ScrollTop>
    </>
  );
};

export default React.memo(ResponsiveAppBar);